import React, { useCallback, useEffect } from 'react';
import { defineMessages, FormattedMessage } from 'react-intl';
import { useHistory, Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { MarketplaceCookieChecker } from '@shipstation/connections-ui';
import { NotificationService, NOTIFICATION_TYPE_ERROR, NOTIFICATION_TYPE_SUCCESS } from 'ui-components/services/notification-service';

import { getPartitionNumber } from '../services/partition-service';
import { LegacyAuth, ResumeSsrActivationPath } from '../constants/cookies';
import { CookieService } from '../services/cookie-service';
import { refreshStoreList } from '../store/seller/seller.actions';
import { SellerProperties } from '../store/seller/seller-properties';
import messages from '../pages/onboard/steps/marketplace-step/messages';
import { useEnableHomePage } from '../feature-flags/hooks/use-enable-home-page';
import { useEnableHomePage2 } from '../feature-flags/hooks/use-enable-home-page2';
import { useIsTrialActive } from './use-is-trial-active';
import { BASE_STORE_SETUP_URL, FTU_WIZARD_PATH, ONBOARD_PATH } from '../routes/constants';
import { useIsSSLiteSwitchCookiePresent } from './use-ss-lite-switch';
import { MarketplaceIds } from '../enums/marketplace-ids';

const notificationMessages = defineMessages({
  errorTitle: {
    defaultMessage: 'Error',
    description: 'Error',
    id: '6/PjjV',
  },
  errorContent: {
    defaultMessage: 'Failed to add {marketplaceName}. Please try again.',
    description: 'Marketplace connection success',
    id: 'abzY5c',
  },
  successTitle: {
    defaultMessage: 'Success',
    description: 'Success',
    id: 'yg2HFT',
  },
  successContent: {
    defaultMessage: 'Successfully added {marketplaceName}.',
    description: 'Marketplace connection success',
    id: 'co/iPJ',
  },
});

const ShopifySuccessNotificationConfig = {
  notificationType: NOTIFICATION_TYPE_SUCCESS,
  title: <FormattedMessage {...messages.saveSuccessTitle} />,
  content: (
    <FormattedMessage {...messages.saveSuccessMessage} values={{ a: (chunks) => <Link to='/orders/awaiting-shipment'>{chunks}</Link> }} />
  ),
  dontHide: true,
};

const GenericSuccessNotificationConfig = {
  notificationType: NOTIFICATION_TYPE_SUCCESS,
  title: <FormattedMessage {...notificationMessages.successTitle} />,
  duration: 3000,
};

const showSuccessNotification = (marketplaceName, marketplaceId) => {
  NotificationService.showNotification({
    ...GenericSuccessNotificationConfig,
    content: <FormattedMessage {...notificationMessages.successContent} values={{ marketplaceName }} />,
  });

  if (String(marketplaceId) === String(MarketplaceIds.Shopify)) {
    NotificationService.showNotification(ShopifySuccessNotificationConfig);
  }
};

const showErrorNotification = (marketplaceName) => {
  NotificationService.showNotification({
    notificationType: NOTIFICATION_TYPE_ERROR,
    title: <FormattedMessage {...notificationMessages.errorTitle} />,
    content: <FormattedMessage {...notificationMessages.errorContent} values={{ marketplaceName }} />,
    duration: 3000,
  });
};

// Determine redirect path based on feature flags and trial status
export const getRedirectPath = ({ enableHomePage, enableHomePage2, isSetupComplete, isTrialActive, resumeSsrActivationPath }) => {
  if (resumeSsrActivationPath) {
    return resumeSsrActivationPath;
  }

  // Old onboarding flow: both home page flags are false and setup incomplete
  if (!enableHomePage && !enableHomePage2 && !isSetupComplete) {
    return ONBOARD_PATH;
  }

  // FTU Wizard: home page 2 enabled and trial active
  if (enableHomePage2 && isTrialActive) {
    return FTU_WIZARD_PATH;
  }

  // Store settings: all other cases
  return BASE_STORE_SETUP_URL;
};

export const useMarketplaceCookieChecker = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const isSetupComplete = useSelector((state) => new SellerProperties({ state }).isSetupComplete);
  const enableHomePage = useEnableHomePage();
  const enableHomePage2 = useEnableHomePage2();
  const isTrialActive = useIsTrialActive();
  const resumeSsrActivationPath = CookieService.getCookieValue(ResumeSsrActivationPath);

  const redirectToPath = getRedirectPath({
    enableHomePage,
    enableHomePage2,
    isSetupComplete,
    isTrialActive,
    resumeSsrActivationPath,
  });

  const isSSLiteSwitchCookiePresent = useIsSSLiteSwitchCookiePresent();

  const conditionallyRedirect = useCallback(() => {
    if (isSSLiteSwitchCookiePresent) {
      return;
    }
    history.push(redirectToPath);
  }, [history, isSSLiteSwitchCookiePresent, redirectToPath]);

  useEffect(() => {
    MarketplaceCookieChecker(
      getPartitionNumber(),
      CookieService.getCookieValue(LegacyAuth),
      (data) => {
        conditionallyRedirect();
        dispatch(refreshStoreList());
        showSuccessNotification(data.name, data.marketplaceId);
      },
      (data) => {
        conditionallyRedirect();
        showErrorNotification(data.name);
      },
    );
  }, [conditionallyRedirect, dispatch]);
};
